import supabase from '../supabase/supabase';

export async function signInAdmin(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password
  });

  if (error) {
    throw new Error('The email or password is incorrect.');
  }

  const authorized = await verifyAdminAccess();

  if (!authorized) {
    await supabase.auth.signOut();
    throw new Error('This account is not authorized to access ZEVI administration.');
  }

  return data;
}

export async function signOutAdmin() {
  const { error } = await supabase.auth.signOut();

  if (error) {
    throw new Error('The session could not be closed.');
  }
}

export async function requestPasswordReset(email) {
  const { error } = await supabase.auth.resetPasswordForEmail(email);

  if (error) {
    throw new Error('The password-reset request could not be sent.');
  }
}

export async function verifyAdminAccess() {
  const { data, error } = await supabase.rpc('zevi_is_admin_20260925');

  if (error) {
    return false;
  }

  return Boolean(data);
}

export async function getAdminDashboard() {
  const [orders, inventory, movements] = await Promise.all([
    supabase
      .from('zevi_orders_20260925')
      .select('id, order_number, customer_name, grand_total, order_status, payment_status, payment_method, created_at')
      .order('created_at', { ascending: false })
      .limit(20),
    supabase
      .from('zevi_inventory_20260925')
      .select('id, quantity_on_hand, quantity_reserved, quantity_committed, low_stock_threshold, zevi_products_20260925(name, base_sku)')
      .limit(100),
    supabase
      .from('zevi_stock_movements_20260925')
      .select('id, movement_type, quantity, reason, created_at, zevi_products_20260925(name)')
      .order('created_at', { ascending: false })
      .limit(10)
  ]);

  if (orders.error || inventory.error || movements.error) {
    throw new Error('The administration dashboard could not be loaded.');
  }

  const orderRows = orders.data || [];
  const inventoryRows = inventory.data || [];
  const validRevenueStatuses = ['verified', 'paid'];

  return {
    metrics: {
      totalOrders: orderRows.length,
      awaitingReview: orderRows.filter(
        (order) => ['proof_submitted', 'under_review'].includes(order.payment_status)
      ).length,
      confirmedOrders: orderRows.filter(
        (order) => order.order_status === 'confirmed'
      ).length,
      processingOrders: orderRows.filter(
        (order) => order.order_status === 'processing'
      ).length,
      lowStock: inventoryRows.filter((item) => {
        const available = item.quantity_on_hand -
          item.quantity_reserved -
          item.quantity_committed;
        return available > 0 && available <= item.low_stock_threshold;
      }).length,
      outOfStock: inventoryRows.filter((item) => (
        item.quantity_on_hand -
        item.quantity_reserved -
        item.quantity_committed <= 0
      )).length,
      verifiedSales: orderRows
        .filter((order) => (
          validRevenueStatuses.includes(order.payment_status) &&
          !['cancelled', 'rejected', 'refunded'].includes(order.order_status)
        ))
        .reduce((sum, order) => sum + Number(order.grand_total), 0)
    },
    recentOrders: orderRows,
    recentStockActivity: movements.data || []
  };
}

export async function getAdminOrders(filters = {}) {
  let query = supabase
    .from('zevi_orders_20260925')
    .select(`
      *,
      items:zevi_order_items_20260925(*),
      payment:zevi_payments_20260925(*),
      history:zevi_order_status_history_20260925(*)
    `)
    .order('created_at', { ascending: false });

  if (filters.orderStatus) {
    query = query.eq('order_status', filters.orderStatus);
  }

  if (filters.paymentStatus) {
    query = query.eq('payment_status', filters.paymentStatus);
  }

  if (filters.paymentMethod) {
    query = query.eq('payment_method', filters.paymentMethod);
  }

  if (filters.search?.trim()) {
    const search = filters.search.trim();
    query = query.or(
      `order_number.ilike.%${search}%,customer_name.ilike.%${search}%,customer_email.ilike.%${search}%,customer_phone.ilike.%${search}%`
    );
  }

  const { data, error } = await query.limit(100);

  if (error) {
    throw new Error('Orders could not be loaded.');
  }

  return data || [];
}

export async function updateOrderStatus(orderId, orderStatus) {
  const { error } = await supabase
    .from('zevi_orders_20260925')
    .update({
      order_status: orderStatus,
      updated_at: new Date().toISOString()
    })
    .eq('id', orderId);

  if (error) {
    throw new Error('The order status could not be updated.');
  }
}

export async function updatePaymentReview(paymentId, status, reason = '') {
  const { error } = await supabase
    .from('zevi_payments_20260925')
    .update({
      status,
      rejection_reason: reason || null,
      reviewed_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    })
    .eq('id', paymentId);

  if (error) {
    throw new Error('The payment review could not be saved.');
  }
}